import React, { useState, useEffect } from 'react';
import { Check, Banknote, Landmark } from 'lucide-react';

const formatDisplay = (raw) => {
  if (!raw) return '';
  return Number(raw).toLocaleString('en-US');
};

export default function TransactionModal({ open, onClose, onSave, initial, title }) {
  const [form, setForm] = useState({ type: 'add', amount: '', paymentMethod: 'cash', note: '', date: '' });
  const [displayAmount, setDisplayAmount] = useState('');

  useEffect(() => {
    if (initial) {
      const amt = initial.amount || '';
      setForm({
        type: initial.type || 'add',
        amount: amt,
        paymentMethod: initial.paymentMethod || 'cash',
        note: initial.note || '',
        date: initial.date ? initial.date.substring(0, 10) : ''
      });
      setDisplayAmount(amt ? formatDisplay(amt) : '');
    } else {
      const today = new Date().toISOString().substring(0, 10);
      setForm({ type: 'add', amount: '', paymentMethod: 'cash', note: '', date: today });
      setDisplayAmount('');
    }
  }, [initial, open]);

  if (!open) return null;

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setDisplayAmount(raw ? formatDisplay(raw) : '');
    setForm(f => ({ ...f, amount: raw }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-cream rounded-t-3xl p-5 pb-8 animate-slide-up shadow-2xl">
        <div className="w-10 h-1 bg-muted/30 rounded-full mx-auto mb-4" />
        <h3 className="text-base font-bold text-dark mb-4">{title || 'ເພີ່ມລາຍການ'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Type */}
          <div className="grid grid-cols-2 gap-2">
            <button type="button"
              onClick={() => setForm(f => ({ ...f, type: 'add' }))}
              className={`py-2.5 rounded-xl text-sm font-medium transition-all ${form.type === 'add' ? 'bg-red-500 text-white shadow-md' : 'bg-white/60 text-muted'}`}>
              + ເພີ່ມໜີ້
            </button>
            <button type="button"
              onClick={() => setForm(f => ({ ...f, type: 'pay' }))}
              className={`py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${form.type === 'pay' ? 'bg-green-500 text-white shadow-md' : 'bg-white/60 text-muted'}`}>
              <Check size={14} /> ຊຳລະ
            </button>
          </div>

          {/* Amount */}
          <div>
            <label className="label">ຈຳນວນເງິນ (ກີບ)</label>
            <input type="text" inputMode="numeric" placeholder="0"
              value={displayAmount}
              onChange={handleAmountChange}
              className="input-field text-lg font-semibold" required />
          </div>

          {/* Payment Method */}
          <div>
            <label className="label">ວິທີຊຳລະ</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button"
                onClick={() => setForm(f => ({ ...f, paymentMethod: 'cash' }))}
                className={`py-2 rounded-xl text-sm transition-all flex items-center justify-center gap-1.5 ${form.paymentMethod === 'cash' ? 'bg-blue-500 text-white' : 'bg-white/60 text-muted'}`}>
                <Banknote size={15} /> ເງິນສົດ
              </button>
              <button type="button"
                onClick={() => setForm(f => ({ ...f, paymentMethod: 'transfer' }))}
                className={`py-2 rounded-xl text-sm transition-all flex items-center justify-center gap-1.5 ${form.paymentMethod === 'transfer' ? 'bg-purple-500 text-white' : 'bg-white/60 text-muted'}`}>
                <Landmark size={15} /> ໂອນ
              </button>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="label">ວັນທີ</label>
            <input type="date" value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              className="input-field" />
          </div>

          {/* Note */}
          <div>
            <label className="label">ໝາຍເຫດ (ບໍ່ບັງຄັບ)</label>
            <input type="text" placeholder="ບັນທຶກເພີ່ມເຕີມ..."
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              className="input-field" />
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary">ຍົກເລີກ</button>
            <button type="submit" className="btn-primary">ບັນທຶກ</button>
          </div>
        </form>
      </div>
    </div>
  );
}
